import { theme } from "antd";
import React from "react";
import styled, { css, RuleSet } from "styled-components";

import { ThemeName } from "./index";

import { BRAND_BOOK_COLORS } from "./configs/equipmentshare";
import {
  INTER_FONT_CSS_HREF,
  TRADE_GOTHIC_NEXT_FONT_CSS_HREF,
} from "./constants";

import { TokenProps } from "~/types";

/**
 * For additional animations, see github.com/animate-css/animate.css/blob/main/source/animate.css
 */

type GlobalStylesProps = {
  $reset?: boolean;
  $themeName?: ThemeName;
  $darkMode?: boolean;
};

export const GlobalStyles: React.FC<GlobalStylesProps> = (props) => {
  const { token } = theme.useToken();

  return (
    <React.Fragment>
      {props.$themeName === "EquipmentShare" &&
        !document.querySelector(
          `link[href="${TRADE_GOTHIC_NEXT_FONT_CSS_HREF}"]`
        ) && <link rel="stylesheet" href={TRADE_GOTHIC_NEXT_FONT_CSS_HREF} />}
      {props.$themeName === "DemandCapture" &&
        !document.querySelector(
          `link[href="${INTER_FONT_CSS_HREF}"]`
        ) && <link rel="stylesheet" href={INTER_FONT_CSS_HREF} />}
      <GlobalStylesDiv {...props} $token={token} />
    </React.Fragment>
  );
};

const GlobalStylesDiv = styled.div<GlobalStylesProps & TokenProps>`
  ${({ $reset }) => ($reset === false ? "" : "all: unset;")}
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  span,
  div,
  p {
    text-transform: unset;
    letter-spacing: 0px;
  }

  html,
  body,
  button,
  input,
  textarea,
  select {
    font-family: "Trade Gothic Next LT Pro", sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  input,
  textarea {
    font-size: inherit;
  }

  .ant-steps-item-finish .ant-steps-icon svg {
    color: ${({ $token }) => $token.colorWhite};
  }

  .ant-alert-message {
    whitespace: normal;
  }

  ${({ $themeName, $token }) => {
    // console.info("theme", $token);
    const rules: Array<RuleSet> = [];

    if ($themeName === "EquipmentShare") {
      rules.push(css`
        ::selection {
          background: ${BRAND_BOOK_COLORS.ORANGE_PRIMARY};
          color: ${BRAND_BOOK_COLORS.WHITE};
        }

        .ant-btn {
          border-style: solid;
          border-width: 1px;
          text-decoration: inherit;
          text-transform: uppercase;
          padding: 8px 15px;
          width: 100%
          max-width: 228px;
        }

        input,
        textarea {
          border-width: 0.75px;
          padding: 12px !important;
        }

        .ant-select-selection-placeholder {
          padding-left: 0.5rem !important;
          padding-right: 0.5rem !important;
        }

        .ant-select-clear {
          margin-right: 0.75rem !important;
        }

        .ant-select-auto-complete {
          margin-bottom: 1rem;
        }
        .ant-form-item-explain {
          padding: 0.25rem;
        }
        .ant-form-item-control-input {
          align-items: inherit;
        }

        .ant-select:not(.ant-select-multiple) {
          &.ant-select-single {
            height: 3rem;
          }

          &:hover,
          &.ant-select-open,
          &.ant-select-focused {
            .ant-select-selector {
              border-color: ${$token.colorText} !important;
            }
          }
          .ant-select-selector:has(
              .ant-select-selection-search + .ant-select-selection-item
            ),
          .ant-select-selector:has(.ant-select-selection-search) {
            border-width: 0.75px;
            height: inherit;
            padding: 24px 11px !important;
          }
          .ant-select-selector:not(
              :has(.ant-select-selection-search + .ant-select-selection-item)
            )
            + .ant-select-clear {
            top: 24px;
          }
          .ant-select-selection-search {
            inset-inline-end: 0 !important;
            inset-inline-start: 0 !important;
          }
          .ant-select-selector + .ant-select-arrow,
          .ant-select-arrow + .ant-select-clear {
            top: 24px;
          }
          .ant-select-selection-item {
            padding: 8px 0;
          }
          .ant-select-selector > .ant-select-selection-wrap {
            padding: 0 0;
            & > .ant-select-selection-search > input {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
          }
        }
      `);
    }

    if ($themeName === "T3") {
      rules.push(css``);
    }

    if ($themeName === "DemandCapture") {
      rules.push(css`
        html,
        body,
        button,
        input,
        textarea,
        select {
          font-family: "Inter", sans-serif;
        }
      `);
    }

    if ($themeName === "ESR") {
      rules.push(css`
        /* Smaller form error/help text within ESR theme only */
        .ant-form-item-explain,
        .ant-form-item-explain-error {
          font-size: 12px;
          line-height: 1.25;
        }

        h1,
        h2,
        h3 {
          font-family: "Trade Gothic Next LT Pro Condensed", sans-serif;
          text-transform: uppercase;
          font-weight: 800;
          letter-spacing: 0.35px;
          margin-bottom: 4px;
        }

        h1 {
          font-size: 32px;
        }
        h2 {
          font-size: 24px;
        }
        h3 {
          font-size: 18px;
        }

        @media (min-width: 768px) {
          h1 {
            font-size: 40px;
          }
          h2 {
            font-size: 28px;
          }
          h3 {
            font-size: 20px;
          }
        }

        @media (min-width: 1200px) {
          h1 {
            font-size: 46px;
          }
          h2 {
            font-size: 32px;
          }
          h3 {
            font-size: 24px;
          }
        }
      `);
    }

    return rules;
  }}

  @keyframes fadeInAnimation {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  .fadeInFast {
    animation: fadeInAnimation ease-in-out 200ms;
    animation-fill-mode: forwards;
    animation-iteration-count: 1;
  }

  @keyframes zoomIn {
    from {
      opacity: 0;
      transform: scale3d(0.3, 0.3, 0.3);
    }

    50% {
      opacity: 1;
    }
  }

  .zoomInFast {
    animation: zoomIn ease-in-out 100ms;
    animation-fill-mode: forwards;
    animation-iteration-count: 1;
  }

  @keyframes bounceIn {
    from,
    20%,
    40%,
    60%,
    80%,
    to {
      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    }

    0% {
      opacity: 0;
      transform: scale3d(0.3, 0.3, 0.3);
    }

    20% {
      transform: scale3d(1.1, 1.1, 1.1);
    }

    40% {
      transform: scale3d(0.9, 0.9, 0.9);
    }

    60% {
      opacity: 1;
      transform: scale3d(1.03, 1.03, 1.03);
    }

    80% {
      transform: scale3d(0.97, 0.97, 0.97);
    }

    to {
      opacity: 1;
      transform: scale3d(1, 1, 1);
    }
  }

  .bounceInFast {
    animation: bounceIn ease-in-out 500ms;
    animation-fill-mode: forwards;
    animation-iteration-count: 1;
  }
`;
